/**
 * Share-link API service for evaluation share links (B-2025-0019).
 *
 * Two distinct client patterns:
 *  - Authenticated endpoints (create/list/revoke/extend) → apiClient (Bearer token attached by interceptor)
 *  - Public endpoint (GET /api/share/:token) → plain fetch, no auth required
 */

import { apiClient, API_URL } from './api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CreateShareRequest {
  expires_in_days?: number; // default 30
  allow_video?: boolean;    // default false
}

export interface CreateShareResponse {
  token: string;
  share_url: string;
  expires_at: string; // ISO
  allow_video: boolean;
}

export type ShareStatus = 'active' | 'expired' | 'revoked';

export interface EvaluationShare {
  token: string;
  share_url: string;
  expires_at: string; // ISO
  revoked_at: string | null;
  view_count: number;
  status: ShareStatus;
}

/** Privacy-safe public card returned by GET /api/share/:token */
export interface PublicShareCard {
  overall_score: number;
  form_score: number | null;
  timing_score: number | null;
  template_name: string;
  coaching_summary: string | null;
  created_at: string; // ISO
  display_name: string | null;
  // Public video playback is not yet supported, so the backend currently omits
  // this field. Optional + forward-compatible: when presigned-URL infra exists
  // the backend can populate it and PublicSharePage already renders it.
  video_url?: string | null;
}

// ─── Authenticated helpers ────────────────────────────────────────────────────

/** POST /api/evaluations/{id}/share → CreateShareResponse */
export async function createShare(
  evaluationId: string,
  request: CreateShareRequest = {}
): Promise<CreateShareResponse> {
  const { data } = await apiClient.post<CreateShareResponse>(
    `/evaluations/${evaluationId}/share`,
    {
      expires_in_days: request.expires_in_days ?? 30,
      allow_video: request.allow_video ?? false,
    }
  );
  return data;
}

/**
 * Envelope returned by GET /api/evaluations/{id}/shares.
 *
 * The backend responds with `ShareListResponse` ({evaluation_id, shares}), NOT a
 * bare array — see `backend/app/api/share_routes.py` (`ShareListResponse`).
 */
export interface ShareListResponse {
  evaluation_id: string;
  shares: EvaluationShare[];
}

/**
 * GET /api/evaluations/{id}/shares → unwrapped to the shares array.
 *
 * This client previously declared `get<EvaluationShare[]>` and returned `data`
 * verbatim, so it handed the *envelope object* to callers.  ShareEvaluationModal
 * then called `.filter()` on it and crashed the whole results page with
 * "p.filter is not a function" the moment the share dialog opened.  The generic
 * on `apiClient.get` is an assertion, not a validation, so `tsc` stayed green
 * and the modal's tests mocked this function with array fixtures — both sides
 * were self-consistent and jointly wrong.
 *
 * The array-vs-envelope check below is deliberate belt-and-braces: it keeps a
 * server on either shape from ever crashing the page again.
 */
export async function listShares(evaluationId: string): Promise<EvaluationShare[]> {
  const { data } = await apiClient.get<ShareListResponse | EvaluationShare[]>(
    `/evaluations/${evaluationId}/shares`
  );
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.shares) ? data.shares : [];
}

/** DELETE /api/shares/{token} → void */
export async function revokeShare(token: string): Promise<void> {
  await apiClient.delete(`/shares/${token}`);
}

/** PUT /api/shares/{token} body: { expires_in_days } → EvaluationShare */
export async function extendShare(
  token: string,
  expiresInDays: number
): Promise<EvaluationShare> {
  const { data } = await apiClient.put<EvaluationShare>(`/shares/${token}`, {
    expires_in_days: expiresInDays,
  });
  return data;
}

// ─── Public (unauthenticated) helper ─────────────────────────────────────────

/**
 * GET /api/share/:token — no auth required.
 *
 * We use a plain `fetch` (not apiClient) so the axios interceptor that attaches
 * the Authorization header is bypassed entirely. This is safe for a public
 * endpoint and allows unauthenticated visitors to view shared results.
 *
 * Throws PublicShareError with status 404 or 410 for invalid/expired links.
 */
export class PublicShareError extends Error {
  constructor(
    public readonly status: 404 | 410,
    message: string
  ) {
    super(message);
    this.name = 'PublicShareError';
  }
}

export async function getPublicShare(token: string): Promise<PublicShareCard> {
  const url = `${API_URL}/share/${token}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (response.status === 404) {
    throw new PublicShareError(404, 'Share link not found.');
  }
  if (response.status === 410) {
    throw new PublicShareError(410, 'This share link has expired or been revoked.');
  }
  if (!response.ok) {
    throw new Error(`Unexpected error fetching share: ${response.status}`);
  }

  return response.json() as Promise<PublicShareCard>;
}
